import { useCallback, useEffect, useState } from 'react';

interface TypeaheadOptionValue {
  id: string;
  name: string;
}

interface FormikFieldSetter<FormValues> {
  current: {
    setFieldValue: (field: Extract<keyof FormValues, string>, value: string) => void;
  } | null;
}

interface PendingTypeaheadOptionCreate<FieldName extends string> {
  fieldName: FieldName;
  requestId: string;
}

interface UseCreatedTypeaheadOptionProps<
  FormValues,
  FieldName extends Extract<keyof FormValues, string>,
  Option extends TypeaheadOptionValue
> {
  formikRef: FormikFieldSetter<FormValues>;
  createdOption?: Option;
  createRequestId?: string;
  emptyValue: string;
  onCreateOption: (name: string, requestId: string) => void;
  getOptionValue?: (option: Option) => string;
}

let typeaheadCreateRequestSequence = 0;

const createTypeaheadCreateRequestId = () => {
  typeaheadCreateRequestSequence += 1;

  return `typeahead-create-${typeaheadCreateRequestSequence}`;
};

const isTypeaheadOption = <Option extends TypeaheadOptionValue>(option: Option | null | undefined): option is Option =>
  option !== null && option !== undefined;

export const useCreatedTypeaheadOption = <
  FormValues,
  FieldName extends Extract<keyof FormValues, string>,
  Option extends TypeaheadOptionValue = TypeaheadOptionValue
>({
  formikRef,
  createdOption,
  createRequestId,
  emptyValue,
  onCreateOption,
  getOptionValue,
}: UseCreatedTypeaheadOptionProps<FormValues, FieldName, Option>) => {
  const [selectedOptions, setSelectedOptions] = useState<{ [fieldName: string]: Option | undefined }>({});
  const [pendingCreate, setPendingCreate] = useState<PendingTypeaheadOptionCreate<FieldName> | undefined>();
  const getValue = useCallback((option: Option) => (getOptionValue ? getOptionValue(option) : option.id), [getOptionValue]);

  const setSelectedOption = useCallback((fieldName: FieldName, option?: Option) => {
    setSelectedOptions((currentOptions) => ({
      ...currentOptions,
      [fieldName]: option,
    }));
  }, []);

  const getSelectedOption = useCallback(
    (fieldName: FieldName, value: string, ...options: Array<Option | null | undefined>) =>
      [selectedOptions[fieldName], ...options].filter(isTypeaheadOption).find((option) => getValue(option) === value),
    [getValue, selectedOptions]
  );

  const onChange = useCallback(
    (fieldName: FieldName) => (value: string, option?: Option) => {
      if (formikRef.current) {
        formikRef.current.setFieldValue(fieldName, value);
      }

      setSelectedOption(fieldName, value === emptyValue ? undefined : option);
    },
    [emptyValue, formikRef, setSelectedOption]
  );

  const createOption = useCallback(
    (fieldName: FieldName) => (name: string) => {
      const requestId = createTypeaheadCreateRequestId();

      setPendingCreate({
        fieldName,
        requestId,
      });
      onCreateOption(name, requestId);
    },
    [onCreateOption]
  );

  useEffect(() => {
    if (!createdOption || !pendingCreate || createRequestId !== pendingCreate.requestId || !formikRef.current) {
      return;
    }

    formikRef.current.setFieldValue(pendingCreate.fieldName, getValue(createdOption));
    setSelectedOption(pendingCreate.fieldName, createdOption);
    setPendingCreate(undefined);
  }, [createRequestId, createdOption, formikRef, getValue, pendingCreate, setSelectedOption]);

  return {
    getSelectedOption,
    onChange,
    onCreateOption: createOption,
  };
};
